/**
 * Beds — continuous looping audio whose level is driven by game state each frame.
 *
 * Two flavours share this manager:
 *  - SINGLETON beds (wind, ocean, rain, the local jetpack…): one instance per id.
 *    These are environmental/listener-centric, so they're 2D; when not driven
 *    they fade to silence but stay alive (cheap, no restart click).
 *  - KEYED POSITIONAL emitters (ship boosters, remote players' jets…): MANY
 *    instances of one sound, one per entity (`key`), each a PannerNode at the
 *    entity's render-space position so it ATTENUATES + PANS with distance. These
 *    are reaped when their entity stops being driven (ship stopped boosting / went
 *    out of range / despawned), so we never leak a silent source per entity.
 *
 * Per frame: set() everything audible, then endFrame() — anything not touched
 * fades (singletons) or fades-out-and-reaps (keyed).
 */
import { BEDS } from "./manifest";
import { setPannerPos, type Vec3 } from "./voices";
import type { AudioEngine } from "./engine";
import type { SoundBank } from "./bank";
import {audioTarget} from './automation';

interface Bed {
  src: AudioBufferSourceNode;
  gain: GainNode;
  panner?: PannerNode;
  keyed: boolean; // per-entity (reap when gone) vs singleton (fade + keep)
}

export class BedManager {
  private beds = new Map<string, Bed>();
  private touched = new Set<string>();
  private retiring: { gain: GainNode; src: AudioBufferSourceNode; panner?: PannerNode; until: number }[] = [];

  constructor(private engine: AudioEngine, private bank: SoundBank) {}

  /**
   * Drive a bed toward `level` (0..1 of its manifest ceiling). `opts.rate`
   * pitches the loop; `opts.pos` (render space) positions a spatial bed; `opts.key`
   * makes a per-entity instance of the same sound (distinct keys = distinct,
   * independently-positioned emitters).
   */
  set(id: string, level: number, opts: { rate?: number; pos?: Vec3; key?: string } = {}): void {
    const def = BEDS[id];
    if (!def) return;
    const mapKey = opts.key ? `${id}@${opts.key}` : id;
    this.touched.add(mapKey);
    const ctx = this.engine.ctx;
    let bed = this.beds.get(mapKey);

    if (!bed) {
      if (level <= 0.001) return; // don't spin one up just to be silent
      const buf = this.bank.get(id);
      if (!buf) return; // loading — retry next frame
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      let panner: PannerNode | undefined;
      if (def.spatial) {
        panner = ctx.createPanner();
        panner.panningModel = "equalpower";
        panner.distanceModel = "inverse";
        panner.refDistance = def.refDist ?? 10;
        panner.maxDistance = def.maxDist ?? 500;
        panner.rolloffFactor = def.rolloff ?? 1;
        if (opts.pos) setPannerPos(panner, opts.pos);
        src.connect(panner).connect(gain);
      } else {
        src.connect(gain);
      }
      gain.connect(this.engine.busInput(def.bus ?? "ambient"));
      src.start();
      bed = { src, gain, panner, keyed: !!opts.key };
      this.beds.set(mapKey, bed);
    }

    const target = clamp01(level) * (def.gain ?? 1);
    audioTarget(bed.gain.gain, target, ctx.currentTime, 0.15);
    if (opts.rate != null) audioTarget(bed.src.playbackRate, opts.rate, ctx.currentTime, 0.2);
    if (opts.pos && bed.panner) setPannerPos(bed.panner, opts.pos);
  }

  /** Resolve the frame: untouched singletons fade to silence; untouched keyed
   *  emitters fade out and are reaped. Call once after the frame's set()s. */
  endFrame(): void {
    const now = this.engine.ctx.currentTime;
    for (const [k, bed] of this.beds) {
      if (this.touched.has(k)) continue;
      if (bed.keyed) {
        bed.gain.gain.cancelScheduledValues(now);
        bed.gain.gain.setValueAtTime(Math.max(0.0001, bed.gain.gain.value), now);
        bed.gain.gain.linearRampToValueAtTime(0.0001, now + 0.3);
        this.retiring.push({ gain: bed.gain, src: bed.src, panner: bed.panner, until: now + 0.4 });
        this.beds.delete(k);
      } else {
        audioTarget(bed.gain.gain, 0, now, 0.15);
      }
    }
    this.reap(now);
    this.touched.clear();
  }

  private reap(now: number): void {
    if (this.retiring.length === 0) return;
    this.retiring = this.retiring.filter((r) => {
      if (now < r.until) return true;
      try {
        r.src.stop();
        r.src.disconnect(); r.panner?.disconnect();
      } catch {
        /* already stopped */
      }
      try {
        r.gain.disconnect();
      } catch {
        /* already disconnected */
      }
      return false;
    });
  }

  stopAll(): void {
    for (const bed of this.beds.values()) {
      try {
        bed.src.stop();
        bed.src.disconnect(); bed.panner?.disconnect();
      } catch {
        /* already stopped */
      }
      try {
        bed.gain.disconnect();
      } catch {
        /* already disconnected */
      }
    }
    this.beds.clear();
    for (const r of this.retiring) {
      try {
        r.src.stop();
        r.src.disconnect(); r.panner?.disconnect(); r.gain.disconnect();
      } catch {
        /* ignore */
      }
    }
    this.retiring = [];
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
